import React from 'react';
import { useDispatch } from 'react-redux';
import { Box, Grid, IconButton, MenuItem, Select } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { ConditionPredicate as Comparator } from '@pepperhq/sdk';
import { removeArrayItem, updateArrayItem } from 'lib/helpers';
import { Option } from 'lib/types';
import { toggleCreateVoucherModal } from 'store/campaigns/campaignsAction';
import { CampaignPaper } from '../CampaignPaper';
import { CampaignConditionData, conditionLabels } from '../models/CampaignItem';
import {
    CampaignTriggerCondition,
    campaignTriggerConditionLabel,
    conditionTypeVariants,
    ConditionVariant,
    isConditionType,
    timeConditionLabel
} from '../models/CampaignTriggerCondition';
import { ConditionValue } from './conditions/ConditionValue';

interface CampaignWhenBlockProps {
    onSubmit: (data: CampaignConditionData) => void;
    condition?: CampaignConditionData;
    index: number;
}

const conditions = Object.entries(campaignTriggerConditionLabel).map(([value, label]) => ({ value, label }));
const getTypeVariant = (variant: string) => [{ label: conditionLabels[variant], value: variant }];

export const CampaignWhenBlock: React.FC<CampaignWhenBlockProps> = ({ onSubmit, condition, index }) => {
    const { type: conditionType = '', value: conditionValues = [] } = condition || {};
    const dispatch = useDispatch();

    // Calculated
    const conditionVariants = React.useMemo<Option[][]>(() => {
        if (!isConditionType(conditionType)) {
            return [];
        }
        return conditionTypeVariants[conditionType].reduce((acc, item) => {
            switch (item) {
                case ConditionVariant.time:
                    acc.push(Object.entries(timeConditionLabel).map(([value, label]) => ({ value, label })));
                    break;
                case ConditionVariant.location:
                    acc.push(getTypeVariant(ConditionVariant.location));
                    break;
                case ConditionVariant.audience:
                    acc.push(getTypeVariant(ConditionVariant.audience));
                    break;
                case ConditionVariant.product:
                    acc.push(getTypeVariant(ConditionVariant.product));
                    break;
                case ConditionVariant.category:
                    acc.push(getTypeVariant(ConditionVariant.category));
                    break;
                case ConditionVariant.voucher:
                    acc.push(getTypeVariant(ConditionVariant.voucher));
                    break;
                case ConditionVariant.amount:
                    acc.push(getTypeVariant(ConditionVariant.amount));
                    break;
            }
            return acc;
        }, []);
    }, [conditionType]);
    const isChecked = React.useMemo(
        () =>
            !!conditionValues[conditionValues.length - 1] &&
            !!conditionValues[conditionValues.length - 1].value,
        [conditionValues]
    );

    // Callbacks
    const handleTypeChange = React.useCallback(
        (e: StrictSelectEvent<CampaignTriggerCondition['type']>) => {
            onSubmit({ type: e.target.value, value: [{ value: '', key: '' }] });
        },
        [onSubmit]
    );
    const handleVariantChange = React.useCallback(
        (value: string, i: number) => {
            if (isConditionType(conditionType)) {
                onSubmit({
                    type: conditionType,
                    value: updateArrayItem(conditionValues, i, {
                        comparator: undefined,
                        value: undefined,
                        key: value
                    })
                });
            }
        },
        [conditionType, onSubmit, conditionValues]
    );
    const handleValueChange = React.useCallback(
        (value: string | string[], i: number, comparator?: Comparator) => {
            if (isConditionType(conditionType)) {
                onSubmit({
                    type: conditionType,
                    value: updateArrayItem(conditionValues, i, {
                        value,
                        comparator,
                        key: conditionValues[i].key
                    })
                });
            }
        },
        [conditionValues, conditionType, onSubmit]
    );
    const handleDelete = React.useCallback(
        (i: number) => {
            if (isConditionType(conditionType)) {
                onSubmit({ type: conditionType, value: removeArrayItem(conditionValues, i) });
            }
        },
        [conditionValues, conditionType, onSubmit]
    );
    const handleCreate = React.useCallback(() => {
        if (isConditionType(conditionType)) {
            onSubmit({ type: conditionType, value: [...conditionValues, { value: '', key: '' }] });
        }
    }, [conditionValues, conditionType, onSubmit]);
    const handleVoucherCreate = React.useCallback(
        (voucherTitle: string, innerIndex: number) => {
            toggleCreateVoucherModal([index, innerIndex], voucherTitle)(dispatch);
        },
        [dispatch, index]
    );

    return (
        <CampaignPaper isChecked={isChecked} title="When...">
            <Grid container spacing={2} alignItems="flex-end">
                <Grid item>
                    <Box minWidth={200}>
                        <Select
                            fullWidth
                            value={conditionType}
                            onChange={handleTypeChange}
                            placeholder="Choose a trigger"
                        >
                            {conditions.map(({ value, label }) => (
                                <MenuItem key={`select-condition-type-${value}`} value={value}>
                                    {label}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                </Grid>
                {conditionValues.map((item, i) => (
                    <ConditionValue
                        key={`condition-value-${i}`}
                        value={item}
                        index={i}
                        onValueChange={handleValueChange}
                        conditionVariants={conditionVariants}
                        onVariantChange={handleVariantChange}
                        onDelete={handleDelete}
                        deletable={conditionValues.length > 1}
                        onVoucherCreate={handleVoucherCreate}
                    />
                ))}
                <Box display="flex" alignItems="center" height="100%">
                    {isChecked && (
                        <IconButton onClick={handleCreate} size="large">
                            <AddIcon />
                        </IconButton>
                    )}
                </Box>
            </Grid>
        </CampaignPaper>
    );
};
